import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Sigma, Camera, Sparkles, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";

const URL_BASE = "https://www.inquira.online";
const CANONICAL = `${URL_BASE}/calculus-ai-solver`;

export const Route = createFileRoute("/calculus-ai-solver")({
  component: CalculusAiSolverPage,
  head: () => ({
    meta: [
      { title: "Calculus AI Solver — Step-by-Step Solutions | Inquira" },
      {
        name: "description",
        content:
          "Free calculus AI solver with step-by-step explanations for derivatives, integrals, limits, and series. Snap a photo or type a problem and get worked solutions.",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Calculus AI Solver — Step-by-Step Solutions" },
      {
        property: "og:description",
        content:
          "Solve derivatives, integrals, limits, and series with an AI tutor that shows every step. Photo scan, voice explanations, and practice problems.",
      },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Calculus AI Solver — Step-by-Step Solutions" },
      {
        name: "twitter:description",
        content: "AI calculus solver with worked steps for derivatives, integrals, limits, and series.",
      },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: URL_BASE + "/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Calculus AI Solver",
              item: CANONICAL,
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Can Inquira solve calculus problems step by step?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Inquira breaks every calculus problem into labeled steps — setup, rule applied, algebra, and final answer — so you can follow the reasoning, not just copy the result.",
              },
            },
            {
              "@type": "Question",
              name: "What kinds of calculus can it handle?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Derivatives (power, chain, product, quotient rules), definite and indefinite integrals, u-substitution, integration by parts, limits, L'Hôpital's rule, series convergence, Taylor and Maclaurin expansions, and multivariable partial derivatives.",
              },
            },
            {
              "@type": "Question",
              name: "Can I take a photo of a handwritten calculus problem?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Snap a photo, crop and rotate it, and Inquira's OCR extracts the expression — including handwritten notation — before solving.",
              },
            },
            {
              "@type": "Question",
              name: "Is the calculus solver free?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Free Forever includes 3 AI tutor questions per UTC day and one complimentary homework image scan for eligible new accounts. New Premium terms include 50 daily AI tutor questions and full study access for $9.99/month. Existing subscriber benefits are preserved.",
              },
            },
          ],
        }),
      },
    ],
  }),
});

const TOPICS = [
  {
    title: "Derivatives",
    examples: ["d/dx [x³ · sin(x)]", "Chain rule: d/dx [cos(3x² + 1)]", "Implicit: d/dx of x² + y² = 25"],
  },
  {
    title: "Integrals",
    examples: ["∫ x·eˣ dx (by parts)", "∫ 2x·√(x²+1) dx (u-sub)", "∫₀^π sin²(x) dx"],
  },
  {
    title: "Limits",
    examples: ["lim x→0 sin(x)/x", "lim x→∞ (1 + 1/x)ˣ", "L'Hôpital: lim x→0 (eˣ − 1)/x"],
  },
  {
    title: "Series & Sequences",
    examples: ["Taylor series of eˣ at x = 0", "Ratio test on Σ n!/nⁿ", "Convergence of Σ 1/n²"],
  },
];

function CalculusAiSolverPage() {
  return (
    <main className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Sigma className="w-5 h-5 text-primary" />
            <span className="font-semibold">Calculus AI Solver</span>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-4 pt-12 pb-8 text-center">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
          Calculus AI Solver with Step-by-Step Solutions
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
          Derivatives, integrals, limits, and series — solved with worked steps you can actually learn from.
          Snap a photo of your problem or type it in.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/">
            <Button size="lg" className="gap-2">
              <Camera className="w-4 h-4" /> Solve a problem
            </Button>
          </Link>
          <Link to="/upgrade">
            <Button size="lg" variant="outline" className="gap-2">
              <Sparkles className="w-4 h-4" /> Get Pro
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6">What Inquira can solve</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {TOPICS.map((t) => (
            <div key={t.title} className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-semibold mb-3">{t.title}</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {t.examples.map((ex) => (
                  <li key={ex} className="font-mono">• {ex}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6">How it works</h2>
        <ol className="space-y-4">
          {[
            { icon: Camera, title: "Capture or type", body: "Snap a photo of a textbook or handwritten problem, or type an expression directly." },
            { icon: Sigma, title: "Get worked steps", body: "Each step is labeled with the rule used — chain rule, u-substitution, L'Hôpital — so you learn the method." },
            { icon: Mic, title: "Listen (Pro)", body: "Hear a natural voice narration of the solution with synchronized step highlighting." },
          ].map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex gap-4 rounded-xl border border-border bg-card p-5">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">{title}</h3>
                <p className="text-sm text-muted-foreground">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6">Frequently asked questions</h2>
        <div className="space-y-4">
          {[
            { q: "Can Inquira solve calculus problems step by step?", a: "Yes. Every problem is broken into labeled steps — setup, rule applied, algebra, and final answer — so you can follow the reasoning." },
            { q: "What kinds of calculus can it handle?", a: "Derivatives (power, chain, product, quotient), definite and indefinite integrals, u-substitution, integration by parts, limits, L'Hôpital's rule, series convergence, Taylor expansions, and partial derivatives." },
            { q: "Can I take a photo of a handwritten problem?", a: "Yes — snap, crop, rotate, and the OCR extracts the expression (handwritten notation included) before solving." },
            { q: "Is it free?", a: "Free Forever includes 3 AI tutor questions per UTC day and one complimentary homework image scan for eligible new accounts. New Premium terms include 50 daily AI tutor questions and full study access for $9.99/month. Existing subscriber benefits are preserved." },
          ].map(({ q, a }) => (
            <div key={q} className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-semibold mb-2">{q}</h3>
              <p className="text-sm text-muted-foreground">{a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-12 text-center">
        <div className="rounded-2xl border border-border bg-card p-8">
          <h2 className="text-2xl font-bold mb-2">Ready to solve your first problem?</h2>
          <p className="text-muted-foreground mb-6">Free — no credit card required.</p>
          <Link to="/">
            <Button size="lg" className="gap-2">
              <Camera className="w-4 h-4" /> Try Inquira now
            </Button>
          </Link>
          <p className="mt-6 text-sm text-muted-foreground">
            Working on earlier coursework?{" "}
            <Link to="/algebra-ai-solver" className="font-semibold underline">
              Try the Algebra AI Solver
            </Link>
            .
          </p>
        </div>
      </section>
    </main>
  );
}